import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Alert,
  message,
} from "antd";
import { FaPlus } from "react-icons/fa";
import "./AddRoomModal.css";

const { Text } = Typography;
const { Option } = Select;

const AddRoomModal = ({ open, visible, onCancel, onSuccess }) => {
  const { authData } = useAuth();
  const [form] = Form.useForm();

  // Support both props during migration
  const isOpen = open ?? visible ?? false;

  const [loading, setLoading] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [prices, setPrices] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && authData?.hotelId) {
      fetchRoomTypes();
      fetchPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, authData?.hotelId]);

  const fetchRoomTypes = async () => {
    try {
      const response = await axiosInstance.get(
        `/RoomTypes/GetRoomTypes/${authData.hotelId}`
      );
      setRoomTypes(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      console.error("Error fetching room types:", e);
      message.error("Failed to fetch room types");
      setRoomTypes([]);
    }
  };

  const fetchPrices = async () => {
    try {
      const response = await axiosInstance.get(`/Price/hotel/${authData.hotelId}`);
      setPrices(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      console.error("Error fetching prices:", e);
      message.error("Failed to fetch prices");
      setPrices([]);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const selectedPrice = prices.find(
        (price) => price.roomTypeId === values.roomTypeId
      );

      const payload = {
        hotelId: authData.hotelId,
        roomTypeId: values.roomTypeId,
        roomNumber: values.roomNumber,
        roomName: values.roomName || String(values.roomNumber),
        capacity: values.capacity,
        floor: values.floor,
        // Your API fields vary; keep your current behaviour
        price: selectedPrice?.amount || selectedPrice?.basePrice || 0,
        currency: selectedPrice?.currency || "USD",
      };

      await axiosInstance.post("/Rooms", payload);

      message.success("Room added successfully");
      form.resetFields();
      onSuccess?.();
    } catch (e) {
      console.error("Error adding room:", e);
      setError(e?.response?.data?.message || "Failed to add room. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getPriceForRoomType = (roomTypeId) => {
    const price = prices.find((p) => p.roomTypeId === roomTypeId);
    if (!price) return "No price set";

    const amount = price.amount ?? price.basePrice ?? 0;
    return `${price.currency} ${amount}`;
  };

  return (
    <Modal
      title={
        <Space>
          <FaPlus />
          <Text strong>Add New Room</Text>
        </Space>
      }
      // visible -> open
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={700}
      // destroyOnClose -> destroyOnHidden
      destroyOnHidden
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ capacity: 2, floor: "1" }}
      >
        <Form.Item
          label="Room Type"
          name="roomTypeId"
          rules={[{ required: true, message: "Please select room type" }]}
        >
          <Select
            placeholder="Select room type"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const children = option?.children;
              if (typeof children === "string") {
                return children.toLowerCase().includes(input.toLowerCase());
              }
              if (Array.isArray(children)) {
                return children.some(
                  (child) =>
                    typeof child === "string" &&
                    child.toLowerCase().includes(input.toLowerCase())
                );
              }
              return false;
            }}
          >
            {roomTypes.map((type) => (
              <Option key={type.roomTypeId} value={type.roomTypeId}>
                {type.typeName} ({getPriceForRoomType(type.roomTypeId)})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Room Number"
          name="roomNumber"
          rules={[
            { required: true, message: "Please enter room number" },
            { pattern: /^[0-9A-Za-z]+$/, message: "Only alphanumeric characters allowed" },
          ]}
        >
          <Input placeholder="E.g. 101, 202A" />
        </Form.Item>

        <Form.Item label="Room Name (Optional)" name="roomName">
          <Input placeholder="E.g. Ocean View, Presidential Suite" />
        </Form.Item>

        <Form.Item
          label="Floor"
          name="floor"
          rules={[{ required: true, message: "Please enter floor" }]}
        >
          <Input placeholder="E.g. 1, 2, Ground" />
        </Form.Item>

        <Form.Item
          label="Capacity"
          name="capacity"
          rules={[{ required: true, message: "Please enter capacity" }]}
        >
          <Input type="number" min={1} max={10} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={roomTypes.length === 0 || prices.length === 0}
            >
              Add Room
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>

        {(roomTypes.length === 0 || prices.length === 0) && (
          <Alert
            type="warning"
            message={
              <Text>
                {roomTypes.length === 0 && (
                  <span>
                    No room types available. Please <a href="/room-types">create room types</a>{" "}
                    first.
                  </span>
                )}
                {roomTypes.length > 0 && prices.length === 0 && (
                  <span>
                    No prices configured. Please <a href="/pricing">set prices</a> for room
                    types.
                  </span>
                )}
              </Text>
            }
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
};

export default AddRoomModal;
